import React, { useState, useEffect } from 'react';
import { Upload, X, FileText, CheckCircle, AlertCircle, Sparkles, Eye, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { DocumentAnalyzer, ExtractionResult } from '../../lib/document-analysis';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import EnhancedDocumentReview from './EnhancedDocumentReview';

interface Document {
  id: string;
  name: string;
  url: string;
  status: 'uploading' | 'processing' | 'complete' | 'error';
  error?: string;
  type?: string;
  extractedText?: string;
}

const DOCUMENT_TYPES = [
  'Capability Statement',
  'Past Performance',
  'Technical Documentation',
  'Certification',
  'Financial Document',
  'Other'
] as const;

type DocumentType = typeof DOCUMENT_TYPES[number];

export function Documents() {
  const { profile } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<DocumentType>('Other');

  // AI Extraction state
  const [extracting, setExtracting] = useState<string | null>(null); // document ID being extracted
  const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [viewingText, setViewingText] = useState<{ name: string; text: string } | null>(null);

  useEffect(() => {
    if (profile) {
      loadDocuments();
    }
  }, [profile]);

  const loadDocuments = async () => {
    try {
      if (!profile) return;

      const { data, error } = await supabase
        .from('profile_documents')
        .select('*')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setDocuments(data.map(doc => ({
        id: doc.id,
        name: doc.file_name,
        url: doc.file_url,
        status: 'complete',
        type: doc.document_type,
        extractedText: doc.extracted_text
      })));
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }

      const file = event.target.files[0];
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }

      setUploading(true);

      // Create temporary document entry
      const tempDoc: Document = {
        id: crypto.randomUUID(),
        name: file.name,
        url: '',
        status: 'uploading',
        type: selectedType
      };

      setDocuments(prev => [tempDoc, ...prev]);

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${profile!.id}/${Date.now()}.${fileExt}`;

      const { data: storageData, error: storageError } = await supabase.storage
        .from('profile-documents')
        .upload(fileName, file);

      if (storageError) throw storageError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-documents')
        .getPublicUrl(fileName);

      // Extract text from document
      const extractedText = await DocumentAnalyzer.processFile(file);
      const analyzedData = await DocumentAnalyzer.analyzeDocument(extractedText);

      // Store document info in database
      const { data: docData, error: dbError } = await supabase
        .from('profile_documents')
        .insert({
          profile_id: profile!.id,
          document_type: selectedType,
          file_name: file.name,
          file_url: publicUrl,
          file_size: file.size,
          mime_type: file.type,
          extracted_text: analyzedData.text,
          metadata: {
            originalName: file.name,
            uploadDate: new Date().toISOString(),
            ...analyzedData.metadata
          }
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Update documents state
      setDocuments(prev => prev.map(doc => 
        doc.id === tempDoc.id 
          ? { 
              id: docData.id,
              name: docData.file_name,
              url: docData.file_url,
              status: 'complete',
              type: docData.document_type
            }
          : doc
      ));

      toast.success('Document uploaded successfully');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (event.target.value) {
        event.target.value = '';
      }
    }
  };

  const handleDelete = async (document: Document) => {
    try {
      // Delete from database
      const { error: dbError } = await supabase
        .from('profile_documents')
        .delete()
        .eq('id', document.id);

      if (dbError) throw dbError;

      // Delete from storage if URL exists. Files are uploaded to `${profile.id}/<name>`,
      // so the stored object path includes the profile-id prefix — removing only the bare
      // filename orphaned the file. Storage cleanup is best-effort (the DB row is already
      // gone, so the document is removed from the user's view regardless).
      if (document.url) {
        const basename = document.url.split('/').pop();
        if (basename && profile?.id) {
          const { error: storageError } = await supabase.storage
            .from('profile-documents')
            .remove([`${profile.id}/${basename}`]);
          if (storageError) console.warn('Document storage removal warning:', storageError.message);
        }
      }

      // Remove from state
      setDocuments(prev => prev.filter(d => d.id !== document.id));
      toast.success('Document removed successfully');
    } catch (error) {
      console.error('Error removing document:', error);
      toast.error('Failed to remove document');
    }
  };

  // AI Extraction handler
  const handleExtractToProfile = async (document: Document) => {
    if (!document.extractedText) {
      toast.error('No extracted text available for this document');
      return;
    }

    setExtracting(document.id);
    try {
      toast.loading('Analyzing document with AI...', { id: 'extraction' });

      const result = await DocumentAnalyzer.extractStructuredData(
        document.extractedText,
        {
          documentName: document.name,
          documentType: document.type
        }
      );

      toast.dismiss('extraction');

      if (result.success) {
        setExtractionResult(result);
        setShowExtractionModal(true);
        toast.success('Document analyzed successfully!');
      } else {
        toast.error('Could not extract structured data from this document');
        console.error('Extraction notes:', result.extractionNotes);
      }
    } catch (error: any) {
      toast.dismiss('extraction');
      console.error('Extraction error:', error);
      toast.error(`Extraction failed: ${error.message}`);
    } finally {
      setExtracting(null);
    }
  };

  // View extracted text handler
  const handleViewExtractedText = (document: Document) => {
    if (document.extractedText) {
      setViewingText({ name: document.name, text: document.extractedText });
    } else {
      toast.error('No extracted text available');
    }
  };

  if (loading) {
    return (
      <div className="bg-bw-surface rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bw-surface rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <FileText className="h-5 w-5 mr-2 text-bw-muted" />
        Documents
      </h3>

      {/* AI Extraction Info Box */}
      <div className="mb-6 bg-bw-surface-subtle border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start">
          <Sparkles className="h-5 w-5 text-bw-primary mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-indigo-900">AI-Powered Profile Enhancement</h4>
            <p className="mt-1 text-sm text-bw-primary">
              Upload your capability statements, past performance documents, or resumes.
              Click the <Sparkles className="h-3 w-3 inline" /> icon on any document to extract company info,
              personnel, past performance, and more directly into your profile.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <p className="text-sm text-bw-muted mb-4">
          Supported formats: PDF, DOC, DOCX, XLS, XLSX. Maximum file size: 10MB.
        </p>

        {/* Document Type Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-bw-ink mb-2">
            Document Type
          </label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as DocumentType)}
            className="block w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
          >
            {DOCUMENT_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="relative">
          <input
            type="file"
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            className="hidden"
            id="document-upload"
            disabled={uploading}
          />
          <label
            htmlFor="document-upload"
            className={`flex flex-col items-center justify-center w-full h-32 px-4 transition bg-bw-surface border-2 border-bw-border border-dashed rounded-lg appearance-none cursor-pointer hover:border-gray-400 focus:outline-none ${
              uploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-sm text-bw-muted">Processing document...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="w-12 h-12 text-bw-muted" />
                <p className="mt-4 text-sm text-bw-muted">Click to upload or drag and drop</p>
                <p className="text-xs text-bw-muted">PDF, DOC, DOCX, XLS, or XLSX (max. 10MB)</p>
              </div>
            )}
          </label>
        </div>
      </div>

      <div className="space-y-4">
        {documents.length === 0 ? (
          <div className="text-center py-6 bg-bw-surface-subtle rounded-lg">
            <FileText className="h-12 w-12 text-bw-muted mx-auto mb-3" />
            <p className="text-bw-muted">No documents uploaded yet</p>
          </div>
        ) : (
          documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-4 bg-bw-surface-subtle rounded-lg"
            >
              <div className="flex items-center flex-1 min-w-0">
                <FileText className="h-5 w-5 text-bw-muted flex-shrink-0" />
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-bw-ink truncate">
                    {doc.name}
                  </p>
                  <div className="flex items-center mt-1">
                    <span className="text-xs text-bw-muted mr-2">
                      Type: {doc.type || 'Other'}
                    </span>
                    {doc.status === 'uploading' && (
                      <p className="text-xs text-bw-primary">Uploading...</p>
                    )}
                    {doc.status === 'processing' && (
                      <p className="text-xs text-fit-moderate">Processing...</p>
                    )}
                    {doc.status === 'complete' && (
                      <CheckCircle className="h-4 w-4 text-success" />
                    )}
                    {doc.status === 'error' && (
                      <div className="flex items-center text-urgent">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        <span className="text-xs">{doc.error || 'Upload failed'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="ml-4 flex-shrink-0 flex items-center space-x-1">
                {doc.status === 'complete' && doc.extractedText && (
                  <>
                    {/* View Extracted Text */}
                    <button
                      onClick={() => handleViewExtractedText(doc)}
                      className="p-2 text-bw-muted hover:text-bw-primary rounded-full hover:bg-bw-primary-tint"
                      title="View extracted text"
                    >
                      <Eye className="h-4 w-4" />
                    </button>

                    {/* Extract to Profile */}
                    <button
                      onClick={() => handleExtractToProfile(doc)}
                      disabled={extracting === doc.id}
                      className="p-2 text-bw-muted hover:text-bw-primary rounded-full hover:bg-bw-primary-tint disabled:opacity-50"
                      title="Extract data to profile"
                    >
                      {extracting === doc.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                    </button>
                  </>
                )}

                {doc.status === 'complete' && (
                  <button
                    onClick={() => handleDelete(doc)}
                    className="p-2 text-bw-muted hover:text-urgent rounded-full hover:bg-bw-surface-subtle"
                    title="Delete document"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
                {doc.status === 'error' && (
                  <button
                    onClick={() => handleDelete(doc)}
                    className="p-2 text-urgent hover:text-urgent rounded-full hover:bg-urgent-bg"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* AI Extraction Result Modal */}
      {showExtractionModal && extractionResult && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-bw-surface-subtle0 bg-opacity-75 transition-opacity" onClick={() => setShowExtractionModal(false)} />
            <div className="relative inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-bw-surface shadow-xl rounded-lg">
              <EnhancedDocumentReview
                extractionResult={extractionResult}
                onComplete={() => {
                  setShowExtractionModal(false);
                  setExtractionResult(null);
                }}
                onCancel={() => {
                  setShowExtractionModal(false);
                  setExtractionResult(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* View Extracted Text Modal */}
      {viewingText && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-bw-surface-subtle0 bg-opacity-75 transition-opacity" onClick={() => setViewingText(null)} />
            <div className="relative inline-block w-full max-w-3xl my-8 overflow-hidden text-left align-middle transition-all transform bg-bw-surface shadow-xl rounded-lg">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-medium">Extracted Text: {viewingText.name}</h3>
                <button
                  onClick={() => setViewingText(null)}
                  className="text-bw-muted hover:text-bw-muted"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-bw-ink font-mono bg-bw-surface-subtle p-4 rounded-lg">
                  {viewingText.text}
                </pre>
              </div>
              <div className="px-6 py-4 border-t bg-bw-surface-subtle flex justify-end">
                <button
                  onClick={() => setViewingText(null)}
                  className="px-4 py-2 bg-gray-200 text-bw-ink rounded-md hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}